import React, { useEffect, useMemo, useRef, useState } from "react";
import { canUpdate } from "../../Services/pageAccess.js";
import { Search, Download, ChevronDown, ChevronUp, Users, DollarSign, CreditCard, BadgeDollarSign, Plus, Pencil, Save, X } from "lucide-react";
import { Panel, SimpleTable, StatusPill, Modal } from "../../Shared/ScreenComponents.jsx";
import { downloadCsv, employeeName, formatDate, latestPayProfile, money, EMPLOYMENT_TYPE_OPTIONS, isJuniorType } from "../../Shared/ScreenUtils.jsx";
import { apiPatch, apiPost } from "../../Api/Client.js";
import { payNeedsNewRevision } from "./payRevision.js";
import "../../Styles/ProjectScreen.css";

// Pay is Fixed only; the server accepts no other pay type.
const PAY_TYPE_OPTIONS = ["Fixed"];

const STATUS_OPTIONS = ["Active", "OnBench", "Exited"];

function initForm(emp) {
  return {
    display_name: emp?.display_name || "",
    employee_code: emp?.employee_code || "",
    contact_number: emp?.contact_number || "",
    email: emp?.email || "",
    account: emp?.account || "",
    company_email: emp?.company_email || "",
    department: emp?.department || "",
    position: emp?.position || "",
    manager: emp?.manager || "",
    employment_type: emp?.employment_type || "Full-Time",
    status: emp?.status || "Active",
    joined_on: emp?.joined_on ? emp.joined_on.slice(0, 10) : "",
    city: emp?.city || "",
    college_name: emp?.college_name || "",
    year_of_graduation: emp?.year_of_graduation || "",
    availability_hours: emp?.availability_hours ?? 40,
    slack_username: emp?.slack_username || "",
    onboarding_completed: emp?.onboarding_completed ?? false,
    base_pay: 0,
    pay_type: "Fixed",
    performance_pay: 0,
  };
}

// Declared out here, not inside the screen: a component declared inside is a new component on every keystroke,
// so React rebuilds every input in it and the cursor jumps out after one character (triage FE-06-06).
const FormField = ({ label, children }) => (
  <label style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 12, fontWeight: 600, color: "var(--c-600)" }}>
    {label}
    {children}
  </label>
);

export function EmployeeRegisterScreen({ data, reload }) {
  // Pay is fetched once when the app boots and is not on the polling list, so a
  // tab left open since this morning keeps showing this morning's salaries --
  // silently, and on the one screen where somebody is reading salaries to pay
  // them. Refetch when this screen is opened so what's on it is current.
  const refreshedOnOpen = useRef(false);
  useEffect(() => {
    if (refreshedOnOpen.current || !reload) return;
    refreshedOnOpen.current = true;
    reload(["employees", "payProfiles", "payrollLineItems"]);
  }, [reload]);

  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("employee_code");
  const [sortDir, setSortDir] = useState("asc");
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [editEmp, setEditEmp] = useState(null);
  const [empForm, setEmpForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(() => initForm());
  const [addSaving, setAddSaving] = useState(false);

  const allEmployees = data.employees || [];
  const employees = allEmployees.filter((emp) => {
    const deptName = String(emp.department_name || "").trim().toLowerCase();
    return Boolean(deptName) && !["Not Assigned", "unassigned", "unknown", "-", "none", "null"].includes(deptName);
  });
  const payProfiles = data.payProfiles || [];
  const lineItems = data.payrollLineItems || [];
  const departments = data.activeDepartments || [];
  const positions = data.positions || [];
  const canManageCompanyEmail = Boolean(
    data?.me?.user?.isSuperuser ||
    canUpdate(data?.me, "employee_register")
  );

  const sorted = useMemo(() => {
    // PTRC Slab: ₹200/month For base_pay ≥ ₹25,000 (Non-Intern); ₹300 In February
    const now = new Date();
    const currentMonth = now.getMonth() + 1; // 1–12
    const ptrcAmount = (base) => {
      if (base < 25000) return 0;
      return currentMonth === 2 ? 300 : 200; // Feb = 300, Rest = 200
    };

    const rows = employees.map((emp) => {
      // Always the person's CURRENT pay, whatever month it is. The Finance
      // screens deliberately differ — they show what a given month pays.
      const pay = latestPayProfile(payProfiles, emp.id);
      const base = Number(pay?.base_pay || 0);
      const latest = lineItems.filter((li) => String(li.employee) === String(emp.id)).sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))[0];
      const deduction = latest && Number(latest.deduction_amount) > 0
        ? Number(latest.deduction_amount)
        : (!isJuniorType(emp.employment_type) ? ptrcAmount(base) : 0);
      return {
        id: emp.id,
        employee_code: emp.employee_code || "-",
        display_name: emp.display_name || employeeName(data, emp.id) || "-",
        department: emp.department_name || "-",
        position: emp.position_title || "-",
        employment_type: emp.employment_type || "-",
        base_pay: base,
        pay_type: pay?.pay_type || "-",
        gross: base,
        deduction,
        net: base - deduction,
        status: latest?.status || "Pending",
        joined_on: emp.joined_on,
        contact: emp.contact_number || "-",
        city: emp.city || "-",
      };
    }).filter((row) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return row.employee_code.toLowerCase().includes(q) || row.display_name.toLowerCase().includes(q) || row.department.toLowerCase().includes(q);
    });

    rows.sort((a, b) => {
      const aVal = a[sortKey] ?? "";
      const bVal = b[sortKey] ?? "";
      const cmp = typeof aVal === "number" ? aVal - bVal : String(aVal).localeCompare(String(bVal));
      return sortDir === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [employees, payProfiles, lineItems, search, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const totalPayroll = sorted.reduce((s, r) => s + r.gross, 0);
  const totalNet = sorted.reduce((s, r) => s + r.net, 0);
  const totalDeduction = sorted.reduce((s, r) => s + r.deduction, 0);

  const SortIcon = ({ column }) => sortKey === column ? (sortDir === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />) : null;

  const openView = (row) => {
    const emp = employees.find((e) => String(e.id) === String(row.id)) || row;
    setSelectedEmp(emp);
    setEditEmp(null);
  };

  const openEdit = (emp) => {
    const pay = latestPayProfile(payProfiles, emp.id);
    setEditEmp(emp);
    setEmpForm(initForm({
      ...emp,
      base_pay: Number(pay?.base_pay || 0),
      pay_type: pay?.pay_type || "Fixed",
      performance_pay: Number(pay?.performance_pay || 0),
    }));
    setSelectedEmp(null);
  };

  const closeEdit = () => {
    setEditEmp(null);
    setFlash("");
  };

  const handleFormChange = (field, value) => {
    setEmpForm((prev) => ({ ...prev, [field]: value }));
  };

  const saveEmployee = async () => {
    setSaving(true);
    setFlash("");
    try {
      const payload = {
        display_name: empForm.display_name,
        employee_code: empForm.employee_code,
        contact_number: empForm.contact_number,
        department: empForm.department || null,
        position: empForm.position || null,
        manager: empForm.manager || null,
        employment_type: empForm.employment_type,
        status: empForm.status,
        joined_on: empForm.joined_on || null,
        city: empForm.city,
        college_name: empForm.college_name,
        year_of_graduation: empForm.year_of_graduation ? Number(empForm.year_of_graduation) : null,
        availability_hours: Number(empForm.availability_hours) || 40,
        slack_username: empForm.slack_username,
        onboarding_completed: Boolean(empForm.onboarding_completed),
      };
      await apiPatch(`/Users/EmployeeProfiles/${editEmp.id}/`, payload);
      const originalCompanyEmail = editEmp.company_email || "";
      const nextCompanyEmail = empForm.company_email || "";
      if (canManageCompanyEmail && nextCompanyEmail !== originalCompanyEmail) {
        await apiPatch(`/Users/EmployeeProfiles/${editEmp.id}/company-email/`, {
          company_email: nextCompanyEmail || null,
        });
      }

      const pay = latestPayProfile(payProfiles, editEmp.id);
      const payPayload = {
        base_pay: Number(empForm.base_pay) || 0,
        pay_type: empForm.pay_type || "Fixed",
        performance_pay: Number(empForm.performance_pay) || 0,
      };
      // A change is a new dated revision; the old row stays as it was (pay records are kept).
      if (payNeedsNewRevision(pay, payPayload)) {
        await apiPost("/Users/PayProfiles/", { employee: editEmp.id, effective_at: new Date().toISOString(), ...payPayload });
      }

      reload(["employees", "payProfiles"]);
      setFlash("Employee Updated Successfully.");
      setTimeout(() => { closeEdit(); }, 800);
    } catch (err) {
      const companyEmailError = err?.payload?.company_email;
      setFlash(
        (Array.isArray(companyEmailError) ? companyEmailError[0] : companyEmailError) ||
        err?.payload?.detail ||
        err?.message ||
        "Failed To Save Employee."
      );
    } finally {
      setSaving(false);
    }
  };

  const [addError, setAddError] = useState("");

  const handleAddChange = (field, value) => {
    setAddForm((prev) => ({ ...prev, [field]: value }));
  };

  const saveNewEmployee = async () => {
    if (!addForm.display_name.trim()) { setAddError("Display Name Is Required."); return; }
    if (!addForm.employee_code.trim()) { setAddError("Employee Code Is Required."); return; }
    setAddSaving(true);
    setAddError("");
    try {
      const emp = await apiPost("/Users/EmployeeProfiles/", {
        display_name: addForm.display_name.trim(),
        employee_code: addForm.employee_code.trim(),
        contact_number: addForm.contact_number,
        department: addForm.department || null,
        position: addForm.position || null,
        employment_type: addForm.employment_type || "Full-Time",
        status: addForm.status || "Active",
        joined_on: addForm.joined_on || null,
        city: addForm.city,
        college_name: addForm.college_name,
        year_of_graduation: addForm.year_of_graduation ? Number(addForm.year_of_graduation) : null,
        availability_hours: Number(addForm.availability_hours) || 40,
        slack_username: addForm.slack_username,
      });
      if (canManageCompanyEmail && addForm.company_email) {
        await apiPatch(`/Users/EmployeeProfiles/${emp.id}/company-email/`, {
          company_email: addForm.company_email,
        });
      }
      const firstPay = {
        base_pay: Number(addForm.base_pay) || 0,
        pay_type: addForm.pay_type || "Fixed",
        performance_pay: Number(addForm.performance_pay) || 0,
      };
      if (payNeedsNewRevision(null, firstPay)) {
        await apiPost("/Users/PayProfiles/", { employee: emp.id, ...firstPay });
      }
      reload(["employees", "payProfiles"]);
      setAddOpen(false);
      setAddForm(initForm());
    } catch (err) {
      const companyEmailError = err?.payload?.company_email;
      setAddError(
        (Array.isArray(companyEmailError) ? companyEmailError[0] : companyEmailError) ||
        err?.payload?.detail ||
        err?.message ||
        "Failed To Create Employee."
      );
    } finally {
      setAddSaving(false);
    }
  };

  const renderEmpSelect = (value, onChange, label) => (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)}>
      <option value="">{label}</option>
      {employees
        .filter((e) => !editEmp || String(e.id) !== String(editEmp.id))
        .map((e) => <option key={e.id} value={e.id}>{e.display_name} ({e.employee_code})</option>)}
    </select>
  );

  const renderDeptSelect = (value, onChange) => (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)}>
      <option value="">Select Department</option>
      {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
    </select>
  );

  const renderPosSelect = (value, onChange) => (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)}>
      <option value="">Select Position</option>
      {positions.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
    </select>
  );

  return (
    <section className="Screen-Stack" style={{ padding: "24px 28px" }}>
      <div className="Reg-Head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <span style={{ fontSize: 12, color: "var(--c-500)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>HR / Employee Register</span>
          <h1 style={{ margin: "4px 0 0", fontSize: 26, fontWeight: 700, color: "var(--c-900)" }}>Employee Register</h1>
        </div>
        <div className="Reg-Head-Actions" style={{ display: "flex", gap: 8 }}>
          <div className="Search-Box" style={{ width: 260 }}>
            <Search size={16} />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search By Name, ID, Or Department…" style={{ border: 0, outline: "none", flex: 1, fontSize: 13 }} />
          </div>
          <button className="Primary-Button" onClick={() => { setAddForm(initForm()); setAddError(""); setAddOpen(true); }}><Plus size={16} /> Add Employee</button>
          <button className="Outline-Button" onClick={() => downloadCsv(`EmployeeRegister.csv`, ["ATG ID", "Employee Name", "Department", "Type", "Base Pay", "Pay Type", "Gross Pay", "PTRC Deducted", "Net Salary", "Status"], sorted.map((r) => [r.employee_code, r.display_name, r.department, r.employment_type, r.base_pay, r.pay_type, r.gross, r.deduction, r.net, r.status]))}><Download size={14} /> Export CSV</button>
        </div>
      </div>

      <div className="Reg-Kpis" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
        {[
          { icon: <Users size={20} />, label: "Total Employees", value: employees.length, color: "var(--blue-500)" },
          { icon: <DollarSign size={20} />, label: "Total Payroll (Gross)", value: `₹${totalPayroll.toLocaleString()}`, color: "var(--green-500)" },
          { icon: <CreditCard size={20} />, label: "Total Deductions", value: `₹${totalDeduction.toLocaleString()}`, color: "var(--amber-500)" },
          { icon: <BadgeDollarSign size={20} />, label: "Total Net Payout", value: `₹${totalNet.toLocaleString()}`, color: "var(--indigo-500)" },
        ].map((card) => (
          <div key={card.label} style={{ background: "var(--surface)", border: "1px solid var(--c-200)", borderRadius: 12, padding: "16px 20px", display: "flex", alignItems: "center", gap: 14, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
            <div style={{ width: 44, height: 44, borderRadius: 10, background: `${card.color}15`, display: "flex", alignItems: "center", justifyContent: "center", color: card.color }}>{card.icon}</div>
            <div><span style={{ fontSize: 12, color: "var(--c-500)", fontWeight: 500 }}>{card.label}</span><strong style={{ display: "block", fontSize: 20, color: "var(--c-900)", marginTop: 2 }}>{card.value}</strong></div>
          </div>
        ))}
      </div>

      <div style={{ background: "var(--surface)", border: "1px solid var(--c-200)", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "var(--surface-soft)", borderBottom: "1px solid var(--c-200)" }}>
                {[
                  ["employee_code", "ATG ID"],
                  ["display_name", "Employee Name"],
                  ["department", "Department"],
                  ["employment_type", "Type"],
                  ["base_pay", "Base Pay"],
                  ["pay_type", "Pay Type"],
                  ["gross", "Gross Pay"],
                  ["deduction", "PTRC Deducted"],
                  ["net", "Net Salary"],
                  ["status", "Status"],
                ].map(([key, label]) => (
                  <th key={key} onClick={() => toggleSort(key)} style={{ padding: "10px 14px", textAlign: "left", fontWeight: 600, color: "var(--c-600)", cursor: "pointer", whiteSpace: "nowrap", userSelect: "none" }}>
                    {label} <SortIcon column={key} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((row, i) => (
                <tr key={row.id} onClick={() => openView(row)} style={{ cursor: "pointer", borderBottom: "1px Solid var(--c-100)", background: i % 2 === 0 ? "var(--surface)" : "var(--surface-soft)", transition: "Background 0.1s" }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "var(--blue-50)"}
                  onMouseLeave={(e) => e.currentTarget.style.background = i % 2 === 0 ? "var(--surface)" : "var(--surface-soft)"}>
                  <td style={{ padding: "10px 14px", fontWeight: 600, color: "var(--blue-600)" }}>{row.employee_code}</td>
                  <td style={{ padding: "10px 14px", fontWeight: 500, color: "var(--c-900)" }}>{row.display_name}</td>
                  <td style={{ padding: "10px 14px", color: "var(--c-600)" }}>{row.department}</td>
                  <td style={{ padding: "10px 14px" }}><StatusPill tone={isJuniorType(row.employment_type) ? "blue" : row.employment_type === "Full-Time" ? "green" : "gold"}>{isJuniorType(row.employment_type) ? "Junior" : row.employment_type}</StatusPill></td>
                  <td style={{ padding: "10px 14px", fontVariantNumeric: "tabular-nums" }}>₹{row.base_pay.toLocaleString()}</td>
                  <td style={{ padding: "10px 14px", color: "var(--c-500)" }}>{row.pay_type}</td>
                  <td style={{ padding: "10px 14px", fontVariantNumeric: "tabular-nums", fontWeight: 500 }}>₹{row.gross.toLocaleString()}</td>
                  <td style={{ padding: "10px 14px", color: "var(--red-600)", fontVariantNumeric: "tabular-nums" }}>₹{row.deduction.toLocaleString()}</td>
                  <td style={{ padding: "10px 14px", fontWeight: 600, color: "var(--green-600)", fontVariantNumeric: "tabular-nums" }}>₹{row.net.toLocaleString()}</td>
                  <td style={{ padding: "10px 14px" }}><StatusPill tone={row.status === "Paid" ? "green" : "gold"}>{row.status}</StatusPill></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!sorted.length && <div style={{ padding: 40, textAlign: "center", color: "var(--c-400)" }}>No Employees Found Matching Your Search.</div>}
      </div>

      {/* View Detail Modal */}
      {selectedEmp && !editEmp && (
        <div className="Modal-Backdrop" onClick={() => setSelectedEmp(null)}>
          <section className="Modal" onClick={(e) => e.stopPropagation()} style={{ width: "min(600px, calc(100vw - 56px))" }}>
            <div className="Modal-Body">
              <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20, paddingBottom: 16, borderBottom: "1px solid var(--c-200)" }}>
                <div style={{ width: 48, height: 48, borderRadius: "50%", background: "var(--blue-500)", color: "var(--surface)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 700 }}>{(selectedEmp.display_name || "?").charAt(0)}</div>
                <div style={{ flex: 1 }}><h2 style={{ margin: 0, fontSize: 18 }}>{selectedEmp.display_name || "-"}</h2><span style={{ color: "var(--c-500)", fontSize: 13 }}>{selectedEmp.employee_code || ""} · {selectedEmp.department_name || "-"}</span></div>
                <button className="Soft-Button Small" onClick={() => openEdit(selectedEmp)}><Pencil size={14} /> Edit</button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                {[
                  ["Employee Code", selectedEmp.employee_code || "-"],
                  ["Department", selectedEmp.department_name || "-"],
                  ["Position", selectedEmp.position_title || "-"],
                  ["Employment Type", selectedEmp.employment_type || "-"],
                  ["Status", selectedEmp.status || "-"],
                  ["Manager", employeeName(data, selectedEmp.manager) || "-"],
                  ["City", selectedEmp.city || "-"],
                  ["Contact", selectedEmp.contact_number || "-"],
                  ["Workspace Login (not an email)", selectedEmp.account || "-"],
                  ["Company Email", selectedEmp.company_email || "-"],
                  ["Personal/Contact Email", selectedEmp.email || "-"],
                  ["Joined", selectedEmp.joined_on ? formatDate(selectedEmp.joined_on) : "-"],
                  ["College", selectedEmp.college_name || "-"],
                  ["Graduation Year", selectedEmp.year_of_graduation || "-"],
                  ["Availability", `${selectedEmp.availability_hours || 40}h/week`],
                  ["Slack", selectedEmp.slack_username || "-"],
                  ["Onboarding", selectedEmp.onboarding_completed ? "Completed" : "Pending"],
                ].map(([label, value]) => (
                  <div key={label} style={{ padding: "8px 0" }}>
                    <dt style={{ fontSize: 11, color: "var(--c-400)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 2 }}>{label}</dt>
                    <dd style={{ margin: 0, fontSize: 14, color: "var(--c-900)", fontWeight: 500 }}>{value}</dd>
                  </div>
                ))}
              </div>
              {/* Pay Info */}
              {(() => {
                const pay = latestPayProfile(payProfiles, selectedEmp.id);
                if (!pay) return null;
                return (
                  <div style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid var(--c-200)" }}>
                    <h4 style={{ margin: "0 0 8px", fontSize: 14 }}>Compensation</h4>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                      {[["Base Pay", `₹${Number(pay.base_pay).toLocaleString()}`], ["Pay Type", pay.pay_type], ["Performance Pay", `₹${Number(pay.performance_pay).toLocaleString()}`]].map(([l, v]) => (
                        <div key={l} style={{ padding: "6px 0" }}>
                          <dt style={{ fontSize: 11, color: "var(--c-400)", textTransform: "uppercase" }}>{l}</dt>
                          <dd style={{ margin: 0, fontSize: 14, fontWeight: 600, color: "var(--c-900)" }}>{v}</dd>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
              {/* Salary Revision History */}
              {(() => {
                const empPayProfiles = payProfiles.filter((p) => String(p.employee) === String(selectedEmp.id)).sort((a, b) => new Date(b.effective_at || 0) - new Date(a.effective_at || 0));
                if (!empPayProfiles.length) return null;
                return (
                  <div style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid var(--c-200)" }}>
                    <h4 style={{ margin: "0 0 8px", fontSize: 14 }}>Salary Revision History</h4>
                    {empPayProfiles.map((p, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", fontSize: 12, borderBottom: "1px solid var(--c-100)" }}>
                        <span style={{ color: "var(--c-500)" }}>{formatDate(p.effective_at)}</span>
                        <span>₹{Number(p.base_pay || 0).toLocaleString()}</span>
                        <span style={{ color: "var(--c-500)" }}>{p.pay_type} {Number(p.performance_pay || 0) > 0 ? `+ ₹${Number(p.performance_pay).toLocaleString()}` : ""}</span>
                      </div>
                    ))}
                  </div>
                );
              })()}
              <div style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid var(--c-200)", display: "flex", gap: 8 }}>
                <button className="Outline-Button" onClick={() => setSelectedEmp(null)}>Close</button>
              </div>
            </div>
          </section>
        </div>
      )}

      {/* Edit Modal */}
      {editEmp && (
        <div className="Modal-Backdrop" onClick={closeEdit}>
          <section className="Modal Wide" onClick={(e) => e.stopPropagation()} style={{ width: "min(800px, calc(100vw - 56px))" }}>
            <div className="Modal-Body">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, paddingBottom: 12, borderBottom: "1px solid var(--c-200)" }}>
                <h2 style={{ margin: 0, fontSize: 18 }}>Edit Employee: {editEmp.display_name}</h2>
                <button className="Icon-Action" onClick={closeEdit} title="Close"><X size={18} /></button>
              </div>

              {flash && (
                <div style={{ padding: "8px 14px", marginBottom: 12, borderRadius: 6, fontSize: 13, background: flash.includes("Failed") || flash.includes("Error") ? "var(--red-50)" : "var(--green-50)", color: flash.includes("Failed") || flash.includes("Error") ? "var(--red-600)" : "var(--green-600)", border: `1px solid ${flash.includes("Failed") || flash.includes("Error") ? "var(--red-200)" : "var(--green-100)"}` }}>
                  {flash}
                </div>
              )}

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <FormField label="Display Name *">
                  <input type="text" value={empForm.display_name} onChange={(e) => handleFormChange("display_name", e.target.value)} />
                </FormField>
                <FormField label="Employee Code *">
                  <input type="text" value={empForm.employee_code} onChange={(e) => handleFormChange("employee_code", e.target.value)} />
                </FormField>
                <FormField label="Contact Number">
                  <input type="text" value={empForm.contact_number} onChange={(e) => handleFormChange("contact_number", e.target.value)} />
                </FormField>
                <FormField label="Workspace Login (not an email)">
                  <input type="text" value={empForm.account} readOnly />
                </FormField>
                <FormField label="Company Email">
                  <input type="email" value={empForm.company_email} readOnly={!canManageCompanyEmail} onChange={(e) => handleFormChange("company_email", e.target.value)} />
                </FormField>
                <FormField label="Personal/Contact Email">
                  <input type="email" value={empForm.email} readOnly />
                </FormField>
                <FormField label="Department">
                  {renderDeptSelect(empForm.department, (v) => handleFormChange("department", v))}
                </FormField>
                <FormField label="Position">
                  {renderPosSelect(empForm.position, (v) => handleFormChange("position", v))}
                </FormField>
                <FormField label="Manager">
                  {renderEmpSelect(empForm.manager, (v) => handleFormChange("manager", v), "No Manager")}
                </FormField>
                <FormField label="Employment Type">
                  <select value={empForm.employment_type} onChange={(e) => handleFormChange("employment_type", e.target.value)}>
                    {EMPLOYMENT_TYPE_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </FormField>
                <FormField label="Status">
                  <select value={empForm.status} onChange={(e) => handleFormChange("status", e.target.value)}>
                    {STATUS_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </FormField>
                <FormField label="Joined On">
                  <input type="date" value={empForm.joined_on} onChange={(e) => handleFormChange("joined_on", e.target.value)} />
                </FormField>
                <FormField label="City">
                  <input type="text" value={empForm.city} onChange={(e) => handleFormChange("city", e.target.value)} />
                </FormField>
                <FormField label="College Name">
                  <input type="text" value={empForm.college_name} onChange={(e) => handleFormChange("college_name", e.target.value)} />
                </FormField>
                <FormField label="Year Of Graduation">
                  <input type="number" min="1950" max="2030" value={empForm.year_of_graduation} onChange={(e) => handleFormChange("year_of_graduation", e.target.value)} />
                </FormField>
                <FormField label="Availability (Hours/Week)">
                  <input type="number" min="1" max="168" value={empForm.availability_hours} onChange={(e) => handleFormChange("availability_hours", e.target.value)} />
                </FormField>
                <FormField label="Slack Username">
                  <input type="text" value={empForm.slack_username} onChange={(e) => handleFormChange("slack_username", e.target.value)} />
                </FormField>
                <FormField label="Onboarding Completed">
                  <select value={empForm.onboarding_completed ? "true" : "false"} onChange={(e) => handleFormChange("onboarding_completed", e.target.value === "true")}>
                    <option value="false">Pending</option>
                    <option value="true">Completed</option>
                  </select>
                </FormField>
              </div>

              <div style={{ borderTop: "1px solid var(--c-200)", marginTop: 16, paddingTop: 14 }}>
                <h4 style={{ margin: "0 0 10px", fontSize: 14 }}>Compensation</h4>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
                  <FormField label="Base Pay (₹)">
                    <input type="number" min="0" step="0.01" value={empForm.base_pay} onChange={(e) => handleFormChange("base_pay", e.target.value)} />
                  </FormField>
                  <FormField label="Pay Type">
                    <select value={empForm.pay_type} onChange={(e) => handleFormChange("pay_type", e.target.value)}>
                      {PAY_TYPE_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </FormField>
                  <FormField label="Performance Pay (₹)">
                    <input type="number" min="0" step="0.01" value={empForm.performance_pay} onChange={(e) => handleFormChange("performance_pay", e.target.value)} />
                  </FormField>
                </div>
              </div>

              <div style={{ marginTop: 20, paddingTop: 14, borderTop: "1px solid var(--c-200)", display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button className="Outline-Button" onClick={closeEdit}>Cancel</button>
                <button className="Primary-Button" disabled={saving} onClick={saveEmployee}>
                  <Save size={16} /> {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </section>
        </div>
      )}

      {/* Add Employee Modal */}
      {addOpen && (
        <Modal title="Add New Employee" onClose={() => { setAddOpen(false); setAddForm(initForm()); setAddError(""); }}>
          {addError && (
            <div style={{ padding: "8px 14px", marginBottom: 12, borderRadius: 6, fontSize: 13, background: "var(--red-50)", color: "var(--red-600)", border: "1px solid var(--red-200)" }}>
              {addError}
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <FormField label="Display Name *">
              <input type="text" value={addForm.display_name} onChange={(e) => handleAddChange("display_name", e.target.value)} />
            </FormField>
            <FormField label="Employee Code *">
              <input type="text" value={addForm.employee_code} onChange={(e) => handleAddChange("employee_code", e.target.value)} />
            </FormField>
            <FormField label="Contact Number">
              <input type="text" value={addForm.contact_number} onChange={(e) => handleAddChange("contact_number", e.target.value)} />
            </FormField>
            {canManageCompanyEmail && (
              <FormField label="Company Email">
                <input type="email" value={addForm.company_email} onChange={(e) => handleAddChange("company_email", e.target.value)} />
              </FormField>
            )}
            <FormField label="Department">
              {renderDeptSelect(addForm.department, (v) => handleAddChange("department", v))}
            </FormField>
            <FormField label="Position">
              {renderPosSelect(addForm.position, (v) => handleAddChange("position", v))}
            </FormField>
            <FormField label="Employment Type">
              <select value={addForm.employment_type} onChange={(e) => handleAddChange("employment_type", e.target.value)}>
                {EMPLOYMENT_TYPE_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </FormField>
            <FormField label="Status">
              <select value={addForm.status} onChange={(e) => handleAddChange("status", e.target.value)}>
                {STATUS_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </FormField>
            <FormField label="Joined On">
              <input type="date" value={addForm.joined_on} onChange={(e) => handleAddChange("joined_on", e.target.value)} />
            </FormField>
            <FormField label="City">
              <input type="text" value={addForm.city} onChange={(e) => handleAddChange("city", e.target.value)} />
            </FormField>
            <FormField label="College Name">
              <input type="text" value={addForm.college_name} onChange={(e) => handleAddChange("college_name", e.target.value)} />
            </FormField>
            <FormField label="Year Of Graduation">
              <input type="number" min="1950" max="2030" value={addForm.year_of_graduation} onChange={(e) => handleAddChange("year_of_graduation", e.target.value)} />
            </FormField>
            <FormField label="Availability (Hours/Week)">
              <input type="number" min="1" max="168" value={addForm.availability_hours} onChange={(e) => handleAddChange("availability_hours", e.target.value)} />
            </FormField>
            <FormField label="Slack Username">
              <input type="text" value={addForm.slack_username} onChange={(e) => handleAddChange("slack_username", e.target.value)} />
            </FormField>
          </div>

          <div style={{ borderTop: "1px solid var(--c-200)", marginTop: 16, paddingTop: 14 }}>
            <h4 style={{ margin: "0 0 10px", fontSize: 14 }}>Compensation (Optional)</h4>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
              <FormField label="Base Pay (₹)">
                <input type="number" min="0" step="0.01" value={addForm.base_pay} onChange={(e) => handleAddChange("base_pay", e.target.value)} />
              </FormField>
              <FormField label="Pay Type">
                <select value={addForm.pay_type} onChange={(e) => handleAddChange("pay_type", e.target.value)}>
                  {PAY_TYPE_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </FormField>
              <FormField label="Performance Pay (₹)">
                <input type="number" min="0" step="0.01" value={addForm.performance_pay} onChange={(e) => handleAddChange("performance_pay", e.target.value)} />
              </FormField>
            </div>
          </div>

          <div className="Modal-Actions">
            <button className="Outline-Button" onClick={() => { setAddOpen(false); setAddForm(initForm()); setAddError(""); }}>Cancel</button>
            <button className="Primary-Button" disabled={addSaving} onClick={saveNewEmployee}>
              {addSaving ? "Creating..." : "Create Employee"}
            </button>
          </div>
        </Modal>
      )}
    </section>
  );
}
